/**
 * AdminSchemaFieldEditor Component
 *
 * Individual field editor for multi-field schemas in the Admin panel.
 * Supports extended types: string, number, boolean, date, array, object.
 * Features drag-and-drop reordering and expandable constraints.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  STORAGE_TIMEZONE_OPTIONS,
  DATETIME_STORAGE_FORMAT_OPTIONS,
  type DatetimeStorage,
  type StorageTimezone,
  type DatetimeStorageFormat,
} from '@/services/api/schemaApi';

// Extended field types for admin schemas
export type AdminSchemaFieldType = 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object';

export interface AdminSchemaFieldConstraints {
  // Number constraints
  min?: number;
  max?: number;
  precision?: number;
  // String constraints
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: 'email' | 'url' | 'uuid' | 'datetime';
  enum?: string[];
  // Date constraints
  minDate?: string;
  maxDate?: string;
  dateFormat?: string;
  // Array constraints
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
  itemType?: AdminSchemaFieldType;
}

export interface AdminSchemaField {
  id: string;
  name: string;
  type: AdminSchemaFieldType;
  required: boolean;
  description?: string;
  constraints?: AdminSchemaFieldConstraints;
  /**
   * Per-field storage convention for date-shaped fields. Read by the
   * materializer so $match comparisons against this field use the correct
   * timezone + format. Populated only when type === 'date'; ignored for
   * other types. See
   * Documents/Roadmaps/Schema_Driven_Query_Materialization_Roadmap.md.
   */
  storage?: DatetimeStorage;
}

interface AdminSchemaFieldEditorProps {
  field: AdminSchemaField;
  index: number;
  onChange: (field: AdminSchemaField) => void;
  onRemove: () => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  isDragging?: boolean;
  isDragOver?: boolean;
}

const FIELD_TYPES: { value: AdminSchemaFieldType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

const STRING_FORMATS: { value: string; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'uuid', label: 'UUID' },
  { value: 'datetime', label: 'DateTime' },
];

export const AdminSchemaFieldEditor: React.FC<AdminSchemaFieldEditorProps> = ({
  field,
  index,
  onChange,
  onRemove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging = false,
  isDragOver = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [enumInput, setEnumInput] = useState('');

  const updateField = (updates: Partial<AdminSchemaField>) => {
    onChange({ ...field, ...updates });
  };

  const updateConstraints = (updates: Partial<AdminSchemaFieldConstraints>) => {
    onChange({
      ...field,
      constraints: { ...field.constraints, ...updates },
    });
  };

  /**
   * Update one piece of the Date field's storage convention. If `value`
   * clears the relevant field AND no other storage value is set, drop the
   * whole storage block (treats "all empty" as "no storage declared").
   */
  const updateStorage = (updates: Partial<DatetimeStorage>) => {
    const next = { ...(field.storage ?? {}), ...updates } as Partial<DatetimeStorage>;
    const anySet =
      (typeof next.timezone === 'string' && next.timezone.length > 0)
      || (typeof next.format === 'string' && next.format.length > 0)
      || typeof next.hasTimezoneMarker === 'boolean';
    onChange({
      ...field,
      storage: anySet ? (next as DatetimeStorage) : undefined,
    });
  };

  const addEnumValue = () => {
    if (!enumInput.trim()) return;
    const currentEnum = field.constraints?.enum || [];
    if (!currentEnum.includes(enumInput.trim())) {
      updateConstraints({ enum: [...currentEnum, enumInput.trim()] });
    }
    setEnumInput('');
  };

  const removeEnumValue = (value: string) => {
    const currentEnum = field.constraints?.enum || [];
    updateConstraints({ enum: currentEnum.filter((v) => v !== value) });
  };

  // Get type badge color
  const getTypeBadgeColor = (type: AdminSchemaFieldType) => {
    switch (type) {
      case 'string': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'number': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'boolean': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'date': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'array': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400';
      case 'object': return 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={onDragEnd}
      className={cn(
        'border rounded-lg bg-background transition-all',
        isDragging && 'opacity-50 border-dashed border-primary',
        isDragOver && 'border-t-2 border-t-primary',
        isExpanded ? 'shadow-sm' : ''
      )}
    >
      {/* Header Row */}
      <div className="flex items-center gap-2 p-3">
        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0" />

        {/* Field Name */}
        <Input
          value={field.name}
          onChange={(e) => updateField({ name: e.target.value.replace(/\s/g, '_').toLowerCase() })}
          placeholder="field_name"
          className="h-8 w-32 font-mono text-sm"
        />

        {/* Type Select */}
        <Select
          value={field.type}
          onValueChange={(value: AdminSchemaFieldType) => {
            updateField({ type: value, constraints: {} });
          }}
        >
          <SelectTrigger className={cn('h-8 w-28', getTypeBadgeColor(field.type))}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELD_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Required Toggle */}
        <button
          type="button"
          onClick={() => updateField({ required: !field.required })}
          className={cn(
            'px-2 py-1 text-xs rounded-md transition-colors flex-shrink-0',
            field.required
              ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
          )}
        >
          {field.required ? 'Required' : 'Optional'}
        </button>

        {/* Expand/Collapse */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 rounded hover:bg-accent flex-shrink-0"
          title={isExpanded ? 'Collapse' : 'Expand constraints'}
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {/* Delete Button */}
        <button
          type="button"
          onClick={onRemove}
          className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 flex-shrink-0"
          title="Remove field"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Expanded Constraints */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t space-y-3">
          {/* Description */}
          <div className="space-y-1 mt-3">
            <Label className="text-xs text-muted-foreground">Description</Label>
            <Input
              value={field.description || ''}
              onChange={(e) => updateField({ description: e.target.value })}
              placeholder="Field description"
              className="h-8 text-sm"
            />
          </div>

          {/* STRING Constraints */}
          {field.type === 'string' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Min Length</Label>
                  <Input
                    type="number"
                    min={0}
                    value={field.constraints?.minLength ?? ''}
                    onChange={(e) =>
                      updateConstraints({
                        minLength: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    placeholder="Min"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Max Length</Label>
                  <Input
                    type="number"
                    min={0}
                    value={field.constraints?.maxLength ?? ''}
                    onChange={(e) =>
                      updateConstraints({
                        maxLength: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    placeholder="Max"
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Format</Label>
                  <Select
                    value={field.constraints?.format || 'none'}
                    onValueChange={(value) =>
                      updateConstraints({ format: value === 'none' ? undefined : value as AdminSchemaFieldConstraints['format'] })
                    }
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {STRING_FORMATS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Pattern (Regex)</Label>
                  <Input
                    value={field.constraints?.pattern ?? ''}
                    onChange={(e) => updateConstraints({ pattern: e.target.value || undefined })}
                    placeholder="^[A-Z]+$"
                    className="h-8 text-sm font-mono"
                  />
                </div>
              </div>

              {/* Enum Values */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Allowed Values (enum)</Label>
                <div className="flex gap-2">
                  <Input
                    value={enumInput}
                    onChange={(e) => setEnumInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEnumValue())}
                    placeholder="Add value"
                    className="h-8 text-sm flex-1"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addEnumValue}>
                    Add
                  </Button>
                </div>
                {field.constraints?.enum && field.constraints.enum.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {field.constraints.enum.map((value) => (
                      <span
                        key={value}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs rounded"
                      >
                        {value}
                        <button
                          type="button"
                          onClick={() => removeEnumValue(value)}
                          className="hover:text-red-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NUMBER Constraints */}
          {field.type === 'number' && (
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Min</Label>
                <Input
                  type="number"
                  value={field.constraints?.min ?? ''}
                  onChange={(e) =>
                    updateConstraints({ min: e.target.value ? parseFloat(e.target.value) : undefined })
                  }
                  placeholder="Min"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Max</Label>
                <Input
                  type="number"
                  value={field.constraints?.max ?? ''}
                  onChange={(e) =>
                    updateConstraints({ max: e.target.value ? parseFloat(e.target.value) : undefined })
                  }
                  placeholder="Max"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Precision</Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={field.constraints?.precision ?? ''}
                  onChange={(e) =>
                    updateConstraints({
                      precision: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  placeholder="Decimals"
                  className="h-8 text-sm"
                />
              </div>
            </div>
          )}

          {/* BOOLEAN - No constraints */}
          {field.type === 'boolean' && (
            <div className="text-xs text-muted-foreground">
              Boolean fields have no additional constraints
            </div>
          )}

          {/* DATE Constraints */}
          {field.type === 'date' && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Date Format</Label>
                <Input
                  value={field.constraints?.dateFormat ?? ''}
                  onChange={(e) => updateConstraints({ dateFormat: e.target.value || undefined })}
                  placeholder="YYYY-MM-DD"
                  className="h-8 text-sm font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Min Date</Label>
                  <Input
                    type="date"
                    value={field.constraints?.minDate ?? ''}
                    onChange={(e) => updateConstraints({ minDate: e.target.value || undefined })}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Max Date</Label>
                  <Input
                    type="date"
                    value={field.constraints?.maxDate ?? ''}
                    onChange={(e) => updateConstraints({ maxDate: e.target.value || undefined })}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              {/* Storage Convention — required reading by the materializer to
                  format $match comparisons against this field correctly. */}
              <div className="space-y-3 mt-3 pt-3 border-t border-dashed border-border">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-foreground">Storage Convention</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Declares how this field's value is stored on disk. The query
                    engine reads this to build correct time-range comparisons.
                    Set all three together, or leave all three empty.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Timezone</Label>
                    <Select
                      value={field.storage?.timezone ?? '__unset__'}
                      onValueChange={(v) => updateStorage({
                        timezone: v === '__unset__' ? undefined : (v as StorageTimezone),
                      })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="— not set —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unset__">— not set —</SelectItem>
                        {STORAGE_TIMEZONE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Format</Label>
                    <Select
                      value={field.storage?.format ?? '__unset__'}
                      onValueChange={(v) => updateStorage({
                        format: v === '__unset__' ? undefined : (v as DatetimeStorageFormat),
                      })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="— not set —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unset__">— not set —</SelectItem>
                        {DATETIME_STORAGE_FORMAT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={field.storage?.hasTimezoneMarker === true}
                    onCheckedChange={(checked) => updateStorage({
                      hasTimezoneMarker: checked === true ? true : false,
                    })}
                    className="mt-0.5"
                  />
                  <span className="text-muted-foreground text-xs">
                    Stored value carries a timezone marker (ends in <code className="text-[10px]">Z</code> or
                    <code className="text-[10px]"> +/-hh:mm</code>). Uncheck for naive local strings.
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* ARRAY Constraints */}
          {field.type === 'array' && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Min Items</Label>
                  <Input
                    type="number"
                    min={0}
                    value={field.constraints?.minItems ?? ''}
                    onChange={(e) =>
                      updateConstraints({
                        minItems: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    placeholder="0"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Max Items</Label>
                  <Input
                    type="number"
                    min={0}
                    value={field.constraints?.maxItems ?? ''}
                    onChange={(e) =>
                      updateConstraints({
                        maxItems: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    placeholder="No limit"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Item Type</Label>
                  <Select
                    value={field.constraints?.itemType || 'string'}
                    onValueChange={(value: AdminSchemaFieldType) =>
                      updateConstraints({ itemType: value })
                    }
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="string">String</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="boolean">Boolean</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={field.constraints?.uniqueItems ?? false}
                  onChange={(e) => updateConstraints({ uniqueItems: e.target.checked || undefined })}
                  className="rounded border-gray-300"
                />
                <span className="text-muted-foreground">Require unique items</span>
              </label>
            </div>
          )}

          {/* OBJECT - Future feature */}
          {field.type === 'object' && (
            <div className="text-xs text-muted-foreground">
              Object fields support nested structures. Define nested properties after creating the schema.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSchemaFieldEditor;
